
import { useState, useEffect, useRef } from "react";
import { toast } from "@/hooks/use-toast";
import { getConversation, getConversationAudio } from "@/services/ai/elevenLabsService/callStatusService";
import { useMediaQuery } from "@/hooks/use-media-query";

export const useConversationDetails = () => {
  const [selectedConversation, setSelectedConversation] = useState<string | null>(null);
  const [conversationDetails, setConversationDetails] = useState<any>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [currentTab, setCurrentTab] = useState("details");
  const [audioData, setAudioData] = useState<any>(null);
  const [isLoadingAudio, setIsLoadingAudio] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  
  // Check if the device is mobile
  const isMobile = useMediaQuery("(max-width: 768px)");

  // Setup audio listener for play state
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.onplay = () => setIsPlaying(true);
      audioRef.current.onpause = () => setIsPlaying(false);
      audioRef.current.onended = () => setIsPlaying(false);
    }
    
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, [audioRef.current]);

  const handleViewDetails = async (conversationId: string) => {
    // For inline view toggle
    if (selectedConversation === conversationId) {
      setSelectedConversation(null);
      setConversationDetails(null);
      return;
    }
    
    // Always set the selected conversation ID
    setSelectedConversation(conversationId);
    
    // For the drawer/sidebar
    setSidebarOpen(true);
    
    // Load the conversation details
    await loadConversationDetails(conversationId);
  };
  
  const loadConversationDetails = async (conversationId: string) => {
    setIsLoadingDetails(true);
    setAudioData(null); // Reset audio data
    
    try {
      const result = await getConversation(conversationId);
      if (result.success) {
        setConversationDetails(result.conversation);
        
        // If audio was also requested and returned
        if (result.audio) {
          setAudioData(result.audio);
        }
      } else {
        toast({
          title: "Error fetching conversation details",
          description: result.error,
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Error fetching conversation details:", error);
      toast({
        title: "An unexpected error occurred",
        description: error instanceof Error ? error.message : "Failed to fetch conversation details",
        variant: "destructive"
      });
    } finally {
      setIsLoadingDetails(false);
    }
  };
  
  const handleFetchAudio = async (conversationId: string) => {
    if (!conversationId) return;
    
    setIsLoadingAudio(true);
    try {
      const result = await getConversationAudio(conversationId);
      if (result.success && result.audio) {
        setAudioData(result.audio);
        setCurrentTab("audio");
      } else {
        toast({
          title: "Error fetching audio",
          description: result.error || "No audio available for this conversation",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Error fetching conversation audio:", error);
      toast({
        title: "Error fetching audio",
        description: error instanceof Error ? error.message : "An unknown error occurred",
        variant: "destructive"
      });
    } finally {
      setIsLoadingAudio(false);
    }
  };
  
  const handlePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
    }
  };

  return {
    selectedConversation,
    conversationDetails,
    isLoadingDetails,
    currentTab,
    setCurrentTab,
    audioData,
    isLoadingAudio, 
    sidebarOpen,
    setSidebarOpen,
    isMobile,
    isPlaying,
    audioRef,
    handleViewDetails,
    loadConversationDetails,
    handleFetchAudio,
    handlePlayPause
  };
};
